/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace laplace
*/
var laplace = {};

/**
* @name cdf
* @memberof laplace
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/laplace/cdf}
*/
import cdf from './../../../../base/dists/laplace/cdf';
setReadOnly( laplace, 'cdf', cdf );

/**
* @name Laplace
* @memberof laplace
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/laplace/ctor}
*/
import Laplace from './../../../../base/dists/laplace/ctor';
setReadOnly( laplace, 'Laplace', Laplace );

/**
* @name entropy
* @memberof laplace
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/laplace/entropy}
*/
import entropy from './../../../../base/dists/laplace/entropy';
setReadOnly( laplace, 'entropy', entropy );

/**
* @name kurtosis
* @memberof laplace
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/laplace/kurtosis}
*/
import kurtosis from './../../../../base/dists/laplace/kurtosis';
setReadOnly( laplace, 'kurtosis', kurtosis );

/**
* @name logcdf
* @memberof laplace
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/laplace/logcdf}
*/
import logcdf from './../../../../base/dists/laplace/logcdf';
setReadOnly( laplace, 'logcdf', logcdf );

/**
* @name logpdf
* @memberof laplace
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/laplace/logpdf}
*/
import logpdf from './../../../../base/dists/laplace/logpdf';
setReadOnly( laplace, 'logpdf', logpdf );

/**
* @name mean
* @memberof laplace
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/laplace/mean}
*/
import mean from './../../../../base/dists/laplace/mean';
setReadOnly( laplace, 'mean', mean );

/**
* @name median
* @memberof laplace
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/laplace/median}
*/
import median from './../../../../base/dists/laplace/median';
setReadOnly( laplace, 'median', median );

/**
* @name mgf
* @memberof laplace
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/laplace/mgf}
*/
import mgf from './../../../../base/dists/laplace/mgf';
setReadOnly( laplace, 'mgf', mgf );

/**
* @name mode
* @memberof laplace
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/laplace/mode}
*/
import mode from './../../../../base/dists/laplace/mode';
setReadOnly( laplace, 'mode', mode );

/**
* @name pdf
* @memberof laplace
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/laplace/pdf}
*/
import pdf from './../../../../base/dists/laplace/pdf';
setReadOnly( laplace, 'pdf', pdf );

/**
* @name quantile
* @memberof laplace
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/laplace/quantile}
*/
import quantile from './../../../../base/dists/laplace/quantile';
setReadOnly( laplace, 'quantile', quantile );

/**
* @name skewness
* @memberof laplace
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/laplace/skewness}
*/
import skewness from './../../../../base/dists/laplace/skewness';
setReadOnly( laplace, 'skewness', skewness );

/**
* @name stdev
* @memberof laplace
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/laplace/stdev}
*/
import stdev from './../../../../base/dists/laplace/stdev';
setReadOnly( laplace, 'stdev', stdev );

/**
* @name variance
* @memberof laplace
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/laplace/variance}
*/
import variance from './../../../../base/dists/laplace/variance';
setReadOnly( laplace, 'variance', variance );


// EXPORTS //

export default laplace;
